// A game's report: what happened in it, the evaluation through it, and each
// position worth replaying, on its board.

import { useCallback, useEffect, useState } from "react";
import { api, errorText, isSignedOut, readJson } from "../app/api.ts";
import { cap, fmtDate, opponentOf, outcomeOf, speedLabel, type GameInfo } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { KIND, kindOf } from "../app/kinds.ts";
import { STRICTNESS, prefs } from "../app/prefs.ts";
import { goBack, navigate, setAddress } from "../app/router.ts";
import { session } from "../app/session.ts";
import { loadSolved, markSolved, reportCache, reportKey, solvedCache } from "../app/solved.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { winChances } from "../board/scores.ts";
import type { Score } from "../board/lines.ts";
import { PuzzlePanel, type PuzzleData } from "./PuzzlePanel.tsx";
import { BoardPlaceholder } from "./BoardScreen.tsx";
import { OpeningLine } from "./opening-text.tsx";
import { ProgressCard, runAnalysis, type Job } from "./analysis.tsx";

export function GameScreen({ params, query }: ScreenProps) {
  const id = params.id!;
  const color = params.color as "white" | "black";
  // The puzzle the address names, by the ply it was played at.
  const startPly = Number(query.get("ply")) || null;
  const [state, setState] = useState<
    { kind: "loading" } | { kind: "missing" } | { kind: "error"; text: string } | { kind: "ready"; report: any; solved: Set<number> }
  >({ kind: "loading" });
  const [job, setJob] = useState<Job | { error: string } | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    let report = reportCache.get(reportKey(id, color));
    try {
      if (!report) {
        const res = await api(`/api/report/${encodeURIComponent(id)}/${color}`);
        if (res.status === 404) return setState({ kind: "missing" });
        if (!res.ok) throw new Error((await readJson(res)).error || "Could not open that report.");
        report = await readJson(res);
        reportCache.set(reportKey(id, color), report);
      }
      const solved = await loadSolved(id, color);
      setState({ kind: "ready", report, solved });
    } catch (err) {
      if (isSignedOut(err)) return;
      setState({ kind: "error", text: errorText(err) });
    }
  }, [id, color]);

  useEffect(() => {
    void load();
  }, [load]);

  async function analyse() {
    setJob({ status: "queued" });
    try {
      const report = await runAnalysis({ input: id, color, config: STRICTNESS[prefs.strictness] }, setJob);
      reportCache.set(reportKey(id, color), report);
      solvedCache.delete(reportKey(id, color));
      setJob(null);
      void load();
    } catch (err) {
      if (isSignedOut(err)) return;
      setJob({ error: errorText(err) });
    }
  }

  const meta = { title: "Game", nav: "games", back: "/games", backLabel: "Games" };
  const status = job ? "error" in job ? <div className="notice error">{job.error}</div> : <ProgressCard job={job} /> : null;

  if (state.kind !== "ready") {
    return (
      <div className="screen">
        <BackButton meta={meta} />
        {state.kind === "loading" ? (
          <BoardPlaceholder
            status="Opening the report…"
            lead={
              <button type="button" className="btn menu-btn" onClick={() => goBack("/games")}>
                <Icon name="back" />
                Back
              </button>
            }
            slots={
              <>
                <button type="button" className="btn" disabled>
                  <Icon name="prev" />
                  <span className="long">Previous</span>
                  <span className="short">Prev</span>
                </button>
                <button type="button" className="btn advance" disabled>
                  Next
                  <Icon name="next" />
                </button>
              </>
            }
          />
        ) : state.kind === "error" ? (
          <div className="notice error">{state.text}</div>
        ) : (
          <>
            <div className="empty card">
              <h2>Not analysed yet</h2>
              <p>Game {id} is not in your library. It takes about a minute.</p>
              <div className="btn-row">
                <button type="button" className="btn btn-primary" onClick={analyse}>
                  Analyse it now
                </button>
              </div>
            </div>
            {status}
          </>
        )}
      </div>
    );
  }

  const report = state.report;
  const stale = Boolean(report.game_id) && (report.analysis_version || 0) < session.analysisVersion;
  return (
    <div className="screen game-screen">
      <BackButton meta={meta} />
      <GameView
        report={report}
        solved={state.solved}
        startPly={startPly}
        onSolved={(ply) => markSolved(id, color, ply)}
        // The address follows the puzzle, so a reload or a link opens this
        // one and not the first one still unsolved.
        onSelect={(ply) => setAddress({ ply })}
        lead={
          <button type="button" className="btn menu-btn" onClick={() => goBack("/games")}>
            <Icon name="back" />
            Back
          </button>
        }
        head={
          <>
            <GameHead report={report} />
            {stale ? (
              <div className="notice accent">
                <span>Analysed by an older version, so some of what the board can show is missing. </span>
                <div className="btn-row">
                  <button type="button" className="btn btn-sm" onClick={analyse}>
                    <Icon name="refresh" />
                    Re-analyse
                  </button>
                </div>
              </div>
            ) : null}
            {status}
          </>
        }
      />
    </div>
  );
}

function GameHead({ report }: { report: any }) {
  const game: GameInfo = { color: report.color, white: report.white, black: report.black, result: report.result };
  const count = (kind: string) => report.puzzles.filter((p: PuzzleData) => p.kind === kind).length;
  const tactics = count("tactic");
  const only = count("only_move");
  const blunders = count("blunder");
  const openings = count("opening");
  const summary = [
    tactics ? `${tactics} ${tactics === 1 ? "tactic" : "tactics"}` : "",
    only ? `${only} only ${only === 1 ? "move" : "moves"}` : "",
    blunders ? `${blunders} ${blunders === 1 ? "blunder" : "blunders"}` : "",
    openings ? `${openings} ${openings === 1 ? "opening" : "openings"}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const o = outcomeOf(game);
  return (
    <div className="game-head">
      <div>
        <h1>vs {opponentOf(game)}</h1>
        <div className="meta">
          <span className={"res " + o} title={o || report.result}>
            {o ? o[0]!.toUpperCase() : "·"}
          </span>
          <span>
            {cap(o) || report.result} as {report.color}
          </span>
          <span className="faint">·</span>
          <span>{speedLabel(report) || report.event}</span>
          {report.played_at ? (
            <>
              <span className="faint">·</span>
              <span>{fmtDate(report.played_at)}</span>
            </>
          ) : null}
          <span className="faint">·</span>
          <span>{summary || "nothing to replay"}</span>
        </div>
        <OpeningLine report={report} />
      </div>
      {report.game_id ? (
        <a className="btn btn-sm" href={`https://lichess.org/${report.game_id}/${report.color}`} target="_blank" rel="noopener">
          Lichess
          <Icon name="ext" />
        </a>
      ) : null}
    </div>
  );
}

/**
 * A report on screen: the eval graph, the puzzles as a row of pills, and the
 * one that is selected, on its board. Boards you have visited stay mounted so
 * a half-solved puzzle is still where you left it when you come back.
 */
export function GameView({
  report,
  solved,
  startPly,
  onSolved,
  onSelect,
  lead,
  head,
}: {
  report: any;
  solved: Set<number>;
  startPly?: number | null;
  onSolved?: (ply: number) => void;
  onSelect?: (ply: number) => void;
  lead: React.ReactNode;
  head: React.ReactNode;
}) {
  const puzzles: PuzzleData[] = report.puzzles || [];
  const game: GameInfo = {
    game_id: report.game_id,
    color: report.color,
    white: report.white,
    black: report.black,
    result: report.result,
    speed: report.speed,
    time_control: report.time_control,
    played_at: report.played_at,
  };
  const tolerance = report.config?.blunder_tolerance ?? 0.15;
  // Open on the first position still to find, unless the address names one:
  // coming back to a game means finishing what is left of it.
  const [selected, setSelected] = useState(() => {
    const named = startPly ? puzzles.findIndex((p) => p.ply === startPly) : -1;
    if (named !== -1) return named;
    const waiting = puzzles.findIndex((p) => !solved.has(p.ply));
    return waiting === -1 ? 0 : waiting;
  });
  const [visited, setVisited] = useState(() => new Set([selected]));
  const [finished, setFinished] = useState(false);
  const [, redraw] = useState(0);

  const select = useCallback(
    (i: number) => {
      if (i < 0 || i >= puzzles.length) return;
      setSelected(i);
      setFinished(false);
      setVisited((v) => (v.has(i) ? v : new Set(v).add(i)));
    },
    [puzzles.length]
  );

  useEffect(() => {
    const p = puzzles[selected];
    if (p && !finished) onSelect?.(p.ply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, finished]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.target as HTMLElement).closest("input, textarea, select, [contenteditable]")) return;
      if (e.key === "ArrowRight") select(selected + 1);
      else if (e.key === "ArrowLeft") select(selected - 1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, select]);

  if (!puzzles.length) {
    return (
      <div className="game-view">
        {head}
        <EvalGraph report={report} selected={-1} onSelect={() => {}} />
        <div className="empty card">
          <h2>Nothing to replay</h2>
          <p>{emptyReason(report)}</p>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="game-view">
        {head}
        <div className="card finished">
          <h2>That's every position in this game.</h2>
          <p className="muted">The ones you solved are remembered. Come back to the rest any time.</p>
          <div className="btn-row" style={{ justifyContent: "center", marginTop: "16px" }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/")}>
              Keep training
            </button>
            <button type="button" className="btn" onClick={() => navigate("/games")}>
              Games
            </button>
            <button type="button" className="btn" onClick={() => select(puzzles.length - 1)}>
              Back to the last one
            </button>
          </div>
        </div>
      </div>
    );
  }

  const around = (
    <>
      <EvalGraph report={report} selected={selected} onSelect={select} />
      <div className="pills">
        {puzzles.map((p, i) => (
          <button
            key={p.ply}
            type="button"
            className={"pill " + kindOf(p) + (solved.has(p.ply) ? " done" : "")}
            aria-current={i === selected}
            title={`${KIND[kindOf(p)].label} at move ${p.move_number}`}
            onClick={() => select(i)}
          >
            {solved.has(p.ply) ? <Icon name="check" /> : null}
            {i + 1}
          </button>
        ))}
        <span className="spacer" />
        <span className="pos">
          {selected + 1} of {puzzles.length}
        </span>
      </div>
    </>
  );

  return (
    <div className="game-view">
      {[...visited].sort((a, b) => a - b).map((i) => {
        const puzzle = puzzles[i]!;
        const last = i === puzzles.length - 1;
        return (
          <PuzzlePanel
            key={puzzle.ply}
            hidden={i !== selected}
            puzzle={puzzle}
            game={game}
            tolerance={tolerance}
            solved={solved.has(puzzle.ply)}
            repertoireId={report.opening?.repertoire?.id}
            onSolved={() => {
              onSolved?.(puzzle.ply);
              // A pasted game has no library entry, so nobody else owns this set.
              solved.add(puzzle.ply);
              redraw((n) => n + 1);
            }}
            lead={lead}
            slots={(done) => (
              <>
                <button type="button" className="btn" disabled={i === 0} onClick={() => select(i - 1)}>
                  <Icon name="prev" />
                  <span className="long">Previous</span>
                  <span className="short">Prev</span>
                </button>
                <button
                  type="button"
                  className={"btn advance" + (done || solved.has(puzzle.ply) ? " btn-primary" : "")}
                  onClick={() => (last ? setFinished(true) : select(i + 1))}
                >
                  {last ? "Done" : "Next"}
                  <Icon name="next" />
                </button>
              </>
            )}
            sheetTop={
              <div className="game-around">
                {head}
                {around}
              </div>
            }
          />
        );
      })}
    </div>
  );
}

/** An empty report is only useful if it says why it is empty. */
function emptyReason(report: any): string {
  const s = report.skipped || {};
  const bits: string[] = [];
  if (report.rejected_candidates > 0)
    bits.push(`${report.rejected_candidates} position(s) looked promising but had more than one winning answer, so they would not have made honest puzzles`);
  if (s.already_winning > 0) bits.push(`${s.already_winning} slip(s) came from positions you were already winning comfortably, which is not worth drilling`);
  if (s.not_decisive > 0) bits.push(`${s.not_decisive} slip(s) cost you ground but nothing decisive was on offer`);
  return bits.length ? bits.join("; ") + "." : "You did not walk past a win, and nothing you played threw the game away.";
}

/** Eval curve, White upwards, with the puzzles marked and clickable. */
function EvalGraph({ report, selected, onSelect }: { report: any; selected: number; onSelect: (i: number) => void }) {
  const w = 800;
  const hh = 112;
  const curve: Score[] = report.curve || [];
  const x = (i: number) => (curve.length < 2 ? 0 : (i / (curve.length - 1)) * w);
  const y = (score: Score) => ((1 - winChances(score)) / 2) * hh;
  const top = curve.map((s, i) => `${x(i).toFixed(1)},${y(s).toFixed(1)}`).join(" ");
  const [uid] = useState(() => "g" + Math.random().toString(36).slice(2, 8));
  const inBook = report.opening?.book_plies || 0;
  return (
    <div className="graph">
      <svg viewBox={`0 0 ${w} ${hh}`} preserveAspectRatio="none" role="img" aria-label="Evaluation through the game, White upwards">
        <defs>
          <clipPath id={`${uid}-under`}>
            <polygon points={`0,${hh} ${top} ${w},${hh}`} />
          </clipPath>
          <clipPath id={`${uid}-over`}>
            <polygon points={`0,0 ${top} ${w},0`} />
          </clipPath>
        </defs>
        <rect x="0" y="0" width={w} height={hh / 2} fill="var(--graph-white)" clipPath={`url(#${uid}-under)`} />
        <rect x="0" y={hh / 2} width={w} height={hh / 2} fill="var(--graph-black)" clipPath={`url(#${uid}-over)`} />
        <line x1="0" y1={hh / 2} x2={w} y2={hh / 2} stroke="var(--line-2)" strokeWidth="1" />
        <polyline points={top} fill="none" stroke="var(--text-2)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {/* The stretch of the game still in the book, so where the opening
            ended is visible on the curve. */}
        {inBook > 0 && curve.length > 1 ? (
          <rect className="book-band" x="0" y="0" width={x(Math.min(inBook, curve.length - 1)).toFixed(1)} height={hh}>
            <title>In book until move {Math.floor(Math.max(inBook - 1, 0) / 2) + 1}</title>
          </rect>
        ) : null}
        <g>
          {(report.puzzles || []).map((p: PuzzleData, i: number) => {
            const kind = kindOf(p);
            return (
              <circle
                key={p.ply}
                className="mark"
                cx={x(p.ply).toFixed(1)}
                cy={y(curve[p.ply] || { kind: "cp", value: 0 }).toFixed(1)}
                r={i === selected ? 7 : 5}
                fill={`var(--${kind === "only_move" ? "only" : kind})`}
                stroke={i === selected ? "var(--text)" : "var(--surface)"}
                strokeWidth={i === selected ? 2.5 : 1.5}
                vectorEffect="non-scaling-stroke"
                onClick={() => onSelect(i)}
              >
                <title>
                  {KIND[kind].label} at move {p.move_number}: {p.played_san} instead of {p.best_san}
                </title>
              </circle>
            );
          })}
        </g>
      </svg>
      <div className="caption">
        <span>Evaluation through the game, White upwards.</span>
        <span>{report.curve_from_pgn ? "Curve from Lichess's own analysis." : `Curve from ${report.engine}.`}</span>
      </div>
    </div>
  );
}
